import { useContext, useState } from 'react';
import { Alert, IconButton, Snackbar } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { X } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { ActivityApi, ActivityInstance } from '@api';
import { SupervisorContext } from '@/contexts/SupervisorContext';
import { CalendarContext } from '@/contexts/CalendarContext';

/**
 * Krysset som tar bort EN aktivitet ur dag- och veckovyn.
 *
 * Tre val som är värda att känna till:
 *
 * 1. HANDLEDARENS verktyg. Komponenten renderar ingenting alls utanför
 *    handledarläge med vald brukare, och gaten sitter HÄR i stället för hos varje
 *    anropsplats — ett kryss som tar bort dagens aktivitet med ett tryck hör inte
 *    hemma i brukarens egen vy, där ett feltryck är lätt gjort och svårt att förstå.
 *
 * 2. Tar bort DEN DAGENS förekomst (activity-instances), inte hela aktiviteten. En
 *    upprepad aktivitet ska inte försvinna ur alla veckor för att en måndag ställdes
 *    in; hela serien tas fortfarande bort inifrån aktivitetsvyn.
 *
 * 3. Ingen bekräftelse och ingen ångra-knapp (Blommans beslut): raden försvinner, och
 *    det ÄR kvittot. Går borttagningen inte igenom står raden kvar — då, och bara då,
 *    säger vi till, för annars hade handledaren trott att den var borta.
 */

type DeleteInstanceButtonProps = {
  instance: ActivityInstance;
  /** Placering/storlek hos anroparen (kortets hörn, radens slut). */
  sx?: SxProps<Theme>;
  /** Ikonens storlek i px. */
  size?: number;
};

const DeleteInstanceButton = ({ instance, sx, size = 16 }: DeleteInstanceButtonProps) => {
  const { t } = useTranslation();
  const { isSupervising, currentClient } = useContext(SupervisorContext);
  const { refreshWeek } = useContext(CalendarContext);
  const [removing, setRemoving] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!isSupervising || !currentClient) return null;

  const handleDelete = async (event: React.MouseEvent) => {
    // Raden/kortet under krysset navigerar till aktiviteten — utan detta hade
    // borttagningen följts av en navigering till något som just tagits bort.
    event.stopPropagation();
    if (removing) return;
    setRemoving(true);
    setFailed(false);
    try {
      const status = await new ActivityApi().removeActivityInstance({
        activityInstanceId: instance.activityInstanceId,
        supervisedTag: currentClient.tag,
      });
      if (status !== 200) {
        setFailed(true);
        return;
      }
      refreshWeek();
    } catch {
      setFailed(true);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <IconButton
        onClick={handleDelete}
        disabled={removing}
        aria-label={t('activity.removeFromDay', { name: instance.activityName }) as string}
        size="small"
        sx={{ color: 'text.secondary', p: 0.5, ...sx }}
      >
        <X size={size} weight="bold" />
      </IconButton>
      <Snackbar
        open={failed}
        autoHideDuration={5000}
        onClose={() => setFailed(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setFailed(false)}>
          {t('activity.removeFailed')}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DeleteInstanceButton;
